'use client'

import clsx from 'clsx'
import { useParams } from 'next/navigation'
import { useTransition } from 'react'
import { Locale, usePathname, useRouter } from '@/i18n/routing'
import { localeNames, locales } from '@/middleware/i18n-routing'
import { NavItem } from '../navigation/NavItem'
import styles from './LanguageSelector.module.css'

export default function LocaleSwitcherSelect() {
  const router = useRouter()
  const [isPending, startTransition] = useTransition()
  const pathname = usePathname()
  const params = useParams()

  const locale = params.locale as Locale

  function onChange(nextLocale: Locale) {
    startTransition(() => {
      router.replace(
        // @ts-expect-error -- TypeScript will validate that only known `params`
        // are used in combination with a given `pathname`. Since the two will
        // always match for the current route, we can skip runtime checks.
        { pathname, params },
        { locale: nextLocale },
      )
    })
  }

  return (
    <NavItem
      size="small"
      anchor="right"
      item={{
        label: locale,
        subNavigation: locales.map((l) => ({
          label: localeNames[l],
          selected: locale === l,
          onClick: () => !isPending && onChange(l),
        })),
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <GlobeIcon />
        <div style={{ textTransform: 'capitalize' }}>{locale}</div>
      </div>
    </NavItem>
  )
}

export const MobileLocaleSwitcher = () => {
  const router = useRouter()
  const [isPending, startTransition] = useTransition()
  const pathname = usePathname()
  const params = useParams()

  const locale = params.locale as Locale

  function onChange(nextLocale: Locale) {
    startTransition(() => {
      router.replace(
        // @ts-expect-error -- TypeScript will validate that only known `params`
        // are used in combination with a given `pathname`. Since the two will
        // always match for the current route, we can skip runtime checks.
        { pathname, params },
        { locale: nextLocale },
      )
    })
  }
  return (
    <div className={styles.localeSwitcher}>
      <div className={styles.localeSwitcherIcon}>
        <GlobeIcon />
      </div>
      {locales.map((l) => (
        <div
          className={clsx(styles.localeSwitcherItem, locale === l && styles.localeSwitcherItemActive)}
          onClick={() => !isPending && onChange(l)}
          key={l}
        >
          {l}
        </div>
      ))}
    </div>
  )
}

const GlobeIcon = () => {
  return (
    <svg
      width="20"
      fill="#5ccbcb"
      height="100%"
      viewBox="0 0 20 20"
      preserveAspectRatio="xMidYMid meet"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M10.5859 19.1171V15.4726H13.5789C13.4031 15.9343 13.2063 16.3726 12.9836 16.7781C12.2969 18.0367 11.4461 18.857 10.5859 19.1171ZM0.8125 10.5859C0.896875 11.9218 1.26719 13.1781 1.8625 14.3007H4.83438C4.54844 13.157 4.37266 11.9054 4.33281 10.5859H0.8125ZM2.60078 15.4726C3.67188 16.9164 5.15313 18.0367 6.87344 18.6601C6.19141 17.8328 5.6125 16.7453 5.17891 15.4726H2.60078ZM7.01406 16.7781C7.70312 18.0367 8.55156 18.857 9.41406 19.1195V15.4726H6.42109C6.59688 15.9343 6.79375 16.3726 7.01406 16.7781ZM1.82969 5.76246C1.25313 6.86871 0.896875 8.10387 0.8125 9.41403H4.33281C4.37266 8.11793 4.54141 6.88746 4.81797 5.76246H1.82969ZM2.55625 4.59059H5.15547C5.59141 3.28981 6.17734 2.18121 6.87109 1.33981C5.12734 1.97262 3.62969 3.11637 2.55625 4.59059ZM12.9859 3.22184C12.2969 1.96325 11.4484 1.14293 10.5859 0.880432V4.58824H13.6047C13.4219 4.10309 13.2156 3.64606 12.9859 3.22184ZM17.4438 4.59059C16.3703 3.11637 14.8703 1.97262 13.1266 1.33981C13.8203 2.17887 14.4086 3.28981 14.8422 4.59059H17.4438ZM17.3992 15.4726H14.8211C14.3875 16.7453 13.8086 17.8328 13.1266 18.6601C14.8469 18.0367 16.3305 16.9164 17.3992 15.4726ZM19.1875 9.41403C19.1055 8.10387 18.7469 6.86871 18.1703 5.76246H15.182C15.4586 6.88746 15.6273 8.11793 15.6672 9.41403H19.1875ZM14.493 10.5859H10.5859V14.3007H13.9586C14.268 13.157 14.4508 11.8984 14.493 10.5859ZM13.975 5.76246H10.5859V9.41403H14.493C14.4531 8.12496 14.275 6.88981 13.975 5.76246ZM6.04141 14.3007H9.41406V10.5859H5.50703C5.54922 11.8984 5.73203 13.157 6.04141 14.3007ZM15.1656 14.3007H18.1375C18.7328 13.1781 19.1031 11.9218 19.1875 10.5859H15.6672C15.6273 11.9054 15.4516 13.157 15.1656 14.3007ZM5.50703 9.41403H9.41406V5.76246H6.025C5.725 6.88981 5.54688 8.12496 5.50703 9.41403ZM7.01406 3.22184C6.78203 3.64606 6.57578 4.10309 6.39531 4.59059H9.41406V0.882776C8.55391 1.14293 7.70312 1.96325 7.01406 3.22184Z"
        fill="#5ccbcb"
      ></path>
    </svg>
  )
}
